import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Inventory as InventoryIcon,
  SwapVert as SwapVertIcon,
  MenuBook as MenuBookIcon,
  Dashboard as DashboardIcon,
  WarningAmber as WarningIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { AnimatePresence } from "framer-motion";

import CustomAlert from "../../components/ui/CustomAlert";
import IngredientsPage from "./IngredientsPage";
import MovementsPage from "./MovementsPage";
import RecipesPage from "./RecipesPage";
import StockDashboard from "./StockDashboard";

const ACCENT = "#2563eb";
const ACCENT_LIGHT = "#3b82f6";
const GRADIENT =
  "linear-gradient(145deg, #1e3a5f 0%, #1d4ed8 30%, #2563eb 65%, #3b82f6 100%)";

const features = [
  { icon: InventoryIcon, label: "Controle de ingredientes" },
  { icon: SwapVertIcon, label: "Entradas e saidas" },
  { icon: MenuBookIcon, label: "Ficha tecnica" },
  { icon: WarningIcon, label: "Alertas de estoque baixo" },
];

const NAV_ITEMS = [
  { icon: SwapVertIcon, label: "Movimentacoes", desc: "Entradas e saidas" },
  { icon: MenuBookIcon, label: "Produtos para controle de estoque", desc: "Receitas e custos" },
  { icon: InventoryIcon, label: "Ingredientes", desc: "Cadastro e estoque" },
  { icon: DashboardIcon, label: "Dashboard", desc: "Visao geral" },
];

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1, transition: { duration: 0.3 } },
  out: { opacity: 0, transition: { duration: 0.3 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const riseUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 200, damping: 22 },
  },
};

const SimpleStock = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const showAlert = useCallback((message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
    setTimeout(() => setAlertOpen(false), 5000);
  }, []);

  const closeAlert = () => setAlertOpen(false);

  const scrollToContent = () => {
    document
      .getElementById("stock-content")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const renderTab = () => {
    switch (activeTab) {
      case 0:
        return <MovementsPage showAlert={showAlert} />;
      case 1:
        return <RecipesPage showAlert={showAlert} />;
      case 2:
        return <IngredientsPage showAlert={showAlert} />;
      case 3:
        return <StockDashboard showAlert={showAlert} />;
      default:
        return <MovementsPage showAlert={showAlert} />;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <motion.div initial="initial" animate="in" exit="out" variants={pageVariants}>
        {/* HERO */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: { xs: 6, md: 10 },
            pb: { xs: 8, md: 14 },
            background: GRADIENT,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.04,
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <Box
            component={motion.div}
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
            sx={{
              position: "absolute",
              width: 400,
              height: 400,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha("#fff", 0.08)} 0%, transparent 70%)`,
              top: -160,
              right: -100,
            }}
          />

          <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">
              <motion.div variants={riseUp}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ mb: 2.5 }}
                >
                  <Chip
                    label="ESTOQUE SIMPLES"
                    size="small"
                    sx={{
                      backgroundColor: alpha("#fff", 0.15),
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      letterSpacing: "2px",
                      backdropFilter: "blur(8px)",
                      border: `1px solid ${alpha("#fff", 0.2)}`,
                    }}
                  />
                </Stack>
              </motion.div>

              <motion.div variants={riseUp}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  fontWeight={800}
                  align="center"
                  sx={{
                    color: "#fff",
                    mb: 2,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                  }}
                >
                  Controle seu estoque
                  <br />
                  de forma simples
                </Typography>
              </motion.div>

              <motion.div variants={riseUp}>
                <Typography
                  variant="h6"
                  align="center"
                  fontWeight={400}
                  sx={{
                    color: alpha("#fff", 0.7),
                    maxWidth: 500,
                    mx: "auto",
                    mb: 5,
                    lineHeight: 1.6,
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Cadastre ingredientes, registre entradas e saidas, monte fichas
                  tecnicas e acompanhe os custos dos seus produtos.
                </Typography>
              </motion.div>

              <motion.div variants={stagger}>
                <Grid container spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                  {features.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <Grid item key={i}>
                        <motion.div variants={riseUp}>
                          <Chip
                            icon={
                              <Icon
                                sx={{
                                  fontSize: 16,
                                  color: `${ACCENT_LIGHT} !important`,
                                }}
                              />
                            }
                            label={f.label}
                            sx={{
                              backgroundColor: alpha("#fff", 0.1),
                              color: "#fff",
                              fontWeight: 500,
                              fontSize: "0.78rem",
                              border: `1px solid ${alpha("#fff", 0.15)}`,
                              backdropFilter: "blur(4px)",
                              px: 0.5,
                              height: 34,
                            }}
                          />
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>

              <motion.div variants={riseUp}>
                <Stack alignItems="center">
                  <Button
                    onClick={scrollToContent}
                    endIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      color: alpha("#fff", 0.7),
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#fff",
                        backgroundColor: alpha("#fff", 0.08),
                      },
                    }}
                  >
                    Ver estoque
                  </Button>
                </Stack>
              </motion.div>
            </motion.div>
          </Container>

          <Box
            sx={{ position: "absolute", bottom: -1, left: 0, right: 0, lineHeight: 0 }}
          >
            <svg
              viewBox="0 0 1440 80"
              preserveAspectRatio="none"
              style={{ width: "100%", height: 50, display: "block" }}
            >
              <path
                d="M0 80L1440 0V80H0Z"
                fill={theme.palette.background.default}
              />
            </svg>
          </Box>
        </Box>

        {/* NAV TABS */}
        <Box id="stock-content" sx={{ px: { xs: 1, md: 8 }, pt: { xs: 2, md: 4 } }}>
          <CustomAlert
            open={alertOpen}
            message={alertMessage}
            severity={alertSeverity}
            onClose={closeAlert}
          />

          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {NAV_ITEMS.map((item, idx) => {
              const Icon = item.icon;
              const isActive = activeTab === idx;
              return (
                <Grid item xs={6} sm={3} key={idx}>
                  <ButtonBase
                    onClick={() => setActiveTab(idx)}
                    sx={{ width: "100%", borderRadius: 2.5 }}
                  >
                    <Paper
                      elevation={isActive ? 4 : 0}
                      sx={{
                        width: "100%",
                        p: 2,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: isActive
                          ? ACCENT
                          : alpha(theme.palette.divider, 0.3),
                        backgroundColor: isActive
                          ? alpha(ACCENT, 0.06)
                          : theme.palette.background.paper,
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: ACCENT,
                          backgroundColor: alpha(ACCENT, 0.03),
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                      >
                        <Icon
                          sx={{
                            fontSize: 22,
                            color: isActive
                              ? ACCENT
                              : theme.palette.text.secondary,
                          }}
                        />
                        <Box sx={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              color: isActive
                                ? ACCENT
                                : theme.palette.text.primary,
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              display: { xs: "none", sm: "block" },
                            }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                        <ChevronRightIcon
                          sx={{
                            fontSize: 18,
                            color: isActive
                              ? ACCENT
                              : theme.palette.text.disabled,
                            display: { xs: "none", sm: "block" },
                          }}
                        />
                      </Stack>
                    </Paper>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderTab()}
            </motion.div>
          </AnimatePresence>
        </Box>
      </motion.div>
    </LocalizationProvider>
  );
};

export default SimpleStock;
